import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchSubmission } from './api';
import { applyBranding } from './theme';
import { BrandProvider } from './components/BrandContext';
import { PhaseProvider } from './components/PhaseContext';
import type { Assessment, Answers, Section, Question, AnswerOption } from './types';

interface LoadedState {
  assessment: Assessment;
  answers: Answers;
  submissionId: string;
}

export default function AnswersPage() {
  const { submissionId = '' } = useParams();
  const [state, setState] = useState<
    | { phase: 'loading' }
    | { phase: 'error'; error: string }
    | ({ phase: 'ready' } & LoadedState)
  >({ phase: 'loading' });

  useEffect(() => {
    let alive = true;
    fetchSubmission(submissionId)
      .then((data) => {
        if (!alive) return;
        setState({
          phase: 'ready',
          assessment: data.assessment,
          answers: data.answers,
          submissionId: data.submissionId,
        });
      })
      .catch((e) => { if (alive) setState({ phase: 'error', error: e.message }); });
    return () => { alive = false; };
  }, [submissionId]);

  useEffect(() => {
    if (state.phase === 'ready') {
      applyBranding(state.assessment.config.branding, { brand: state.assessment.brand ?? null });
      document.title = `${state.assessment.title} — Your Answers`;
    }
  }, [state]);

  if (state.phase === 'loading') {
    return <div className="loading-screen"><div className="loading-spinner" /><span>Loading your answers...</span></div>;
  }
  if (state.phase === 'error') {
    return (
      <div className="app-shell centered">
        <div className="card">
          <h1>We couldn't load those answers</h1>
          <p className="muted">{state.error}</p>
        </div>
      </div>
    );
  }

  const { assessment, answers } = state;
  const sections: Section[] = assessment.config.sections;
  const branding = assessment.config.branding;
  const effectiveLogoUrl = assessment.brand?.logoUrl ?? branding?.logoUrl ?? null;
  const logoLinkUrl = assessment.brand?.logoLinkUrl ?? null;
  const showLogo = assessment.config.intro.showLogo !== false && !!effectiveLogoUrl;

  function findSelectedOption(q: Question, answer: string | number | boolean | Array<string | number> | undefined): AnswerOption | undefined {
    if (answer == null || Array.isArray(answer)) return undefined;
    return q.options.find((opt) => {
      const val: string | number = opt.value ?? opt.archetype ?? (opt.correct ? 1 : 0);
      return val === answer || String(val) === String(answer);
    });
  }

  return (
    <BrandProvider brand={assessment.brand ?? null}>
    <PhaseProvider phase="result">
    <div className="app-shell centered">
      {showLogo && (
        <div className="brand-bar">
          {logoLinkUrl ? (
            <a href={logoLinkUrl} className="brand-bar-link" aria-label={assessment.brand?.name ?? 'Home'}>
              <img src={effectiveLogoUrl!} alt="" />
            </a>
          ) : (
            <img src={effectiveLogoUrl!} alt="" />
          )}
        </div>
      )}

      <div className="card answers-page">
        <div className="answers-header">
          <h1 className="answers-title">Your Answers</h1>
          <p className="answers-subtitle">{assessment.title}</p>
          <Link to={`/results/${submissionId}`} className="answers-back-link">
            ← Back to your results
          </Link>
        </div>

        {sections.map((section) => (
          <div key={section.id} className="answers-section">
            <div className="answers-section-label">{section.label}</div>
            {section.questions.map((q, qi) => {
              const answer = answers[q.id];
              const selected = findSelectedOption(q, answer);
              return (
                <div key={q.id} className="answers-question">
                  <div className="answers-q-number">{qi + 1}</div>
                  <div className="answers-q-body">
                    <div className="answers-q-text">{q.text}</div>
                    <div className="answers-options">
                      {q.options.map((opt, oi) => {
                        const val = opt.value ?? opt.archetype ?? (opt.correct ? 1 : 0);
                        const isSelected = Array.isArray(answer)
                          ? answer.some((a) => String(a) === String(val))
                          : selected && (val === answer || String(val) === String(answer));
                        return (
                          <div
                            key={oi}
                            className={`answers-option${isSelected ? ' selected' : ''}`}
                          >
                            {opt.icon && <span className="answers-option-icon">{opt.icon}</span>}
                            <span className="answers-option-label">{opt.label}</span>
                            {isSelected && <span className="answers-check">✓</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}

        <div className="answers-footer">
          <Link to={`/results/${submissionId}`} className="btn btn-primary">
            Back to your results
          </Link>
        </div>
      </div>
    </div>
    </PhaseProvider>
    </BrandProvider>
  );
}
